import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, Mail, Send, Users, MousePointerClick, Eye, TrendingUp, AlertTriangle, Ban } from "lucide-react";
import { toast } from "sonner";

/**
 * iter164 — Admin Campaigns panel. Renders on the "Campaigns" tab.
 * Backed by /api/admin/campaigns endpoints. Supports:
 *   - Preview audience for a campaign key
 *   - Create campaign (materialise recipient rows)
 *   - Send-batch (rate-limited during domain warm-up)
 *   - List campaigns with per-campaign stats (sent/opened/clicked/
 *     bounced/spam/unsubscribed/converted)
 *   - Drill into a campaign for the recipient-level status sample.
 */

const CAMPAIGN_DEFS = [
  { key: "recurring-signup", name: "Save on Your First Recurring Lawn Service",
    description: "Target: registered customers without an active weekly, biweekly, or monthly lawn schedule." },
];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
        {Icon && <Icon className="w-3.5 h-3.5 text-emerald-600" />} {label}
      </div>
      <div className="mt-1 text-xl font-extrabold text-gray-900">{value}</div>
    </div>
  );
}

export default function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const [selectedKey, setSelectedKey] = useState("recurring-signup");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [batchLimit, setBatchLimit] = useState(20);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaigns");
      setCampaigns(data.campaigns || []);
    } catch { setCampaigns([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/campaigns/${selectedKey}/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreview(null);
    } finally { setBusy(false); }
  };

  const createCampaign = async () => {
    if (!window.confirm(`Create campaign for ${preview?.audience_size || 0} recipients?\n\nNo emails will be sent yet — you'll click "Send batch" after this to send in rate-limited waves.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${selectedKey}/create`, { note });
      toast.success(`Campaign created (${data.total_recipients} recipients seeded)`);
      setNote("");
      setPreview(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const sendBatch = async (campaign_id, limit) => {
    if (!window.confirm(`Send email to the next ${limit} pending recipients?\n\nThis is a REAL send. Keep batches small (recommended 20/day) during the 14-day domain warm-up.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${campaign_id}/send-batch`, { limit });
      toast.success(`Sent ${data.batch_sent} · failed ${data.batch_failed}`);
      await refresh();
      if (selectedId === campaign_id) await openDetail(campaign_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const openDetail = async (campaign_id) => {
    setSelectedId(campaign_id);
    try {
      const { data } = await api.get(`/admin/campaigns/${campaign_id}/detail`);
      setDetail(data);
    } catch { setDetail(null); }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-panel">
      {/* Warm-up warning banner */}
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="campaigns-warmup-banner">
        <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900">
          <div className="font-bold text-sm">Domain warm-up in progress</div>
          <div className="mt-1">
            <b>jdsnlawncare.com</b> is still building sender reputation with Gmail.
            Send in small batches — ideally <b>20/day</b> for the first 14 days.
            Larger blasts can permanently degrade inbox placement.
          </div>
        </div>
      </div>

      {/* Preview + create */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="campaigns-preview-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">
          <Mail className="w-3.5 h-3.5" /> Create a campaign
        </div>
        <div className="mt-4 grid gap-3">
          {CAMPAIGN_DEFS.map((c) => (
            <button key={c.key} type="button"
                    onClick={() => { setSelectedKey(c.key); setPreview(null); }}
                    data-testid={`campaign-def-${c.key}`}
                    className={`text-left p-3 rounded-xl border transition ${selectedKey === c.key ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:bg-gray-50"}`}>
              <div className="text-sm font-extrabold text-gray-900">{c.name}</div>
              <div className="text-xs text-gray-600 mt-0.5">{c.description}</div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={loadPreview} disabled={busy}
                  data-testid="campaign-preview-btn"
                  className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Preview audience
          </Button>
        </div>

        {preview && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="campaign-preview-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-extrabold text-gray-900">
                Audience: {preview.audience_size} eligible recipients
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">
                Filters excluded: unsubscribed, bounced, archived, active recurring, test domains
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Sample: {(preview.sample || []).slice(0, 5).map((s) => s.email).join(", ")}{(preview.sample || []).length > 5 ? "…" : ""}
            </div>
            <Textarea
              placeholder="Optional note (e.g. 'Aug 2026 first push')"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-3"
              data-testid="campaign-create-note"
            />
            <div className="mt-3 flex gap-2">
              <Button onClick={createCampaign} disabled={busy || preview.audience_size === 0}
                      data-testid="campaign-create-btn"
                      className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white">
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Create campaign
              </Button>
              <Button variant="outline" onClick={() => { setPreview(null); setNote(""); }}
                      className="rounded-full" data-testid="campaign-preview-cancel">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* List existing */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="campaigns-list-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">
          <TrendingUp className="w-3.5 h-3.5" /> Campaigns
        </div>
        {loading && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500" data-testid="campaigns-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && campaigns.length === 0 && (
          <div className="mt-4 text-sm text-gray-500" data-testid="campaigns-empty">
            No campaigns yet. Create one above to get started.
          </div>
        )}
        {!loading && campaigns.map((c) => (
          <div key={c.campaign_id} className="mt-4 rounded-xl border border-gray-200 overflow-hidden" data-testid={`campaign-row-${c.campaign_id}`}>
            <div className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-extrabold text-gray-900">{c.name}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    Created {new Date(c.created_at).toLocaleString()} · by {c.created_by || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="number" min={1} max={500} value={batchLimit}
                         onChange={(e) => setBatchLimit(parseInt(e.target.value || "20", 10))}
                         className="w-20 px-2 py-1 border rounded-lg text-sm"
                         data-testid={`campaign-batch-limit-${c.campaign_id}`} />
                  <Button size="sm" onClick={() => sendBatch(c.campaign_id, batchLimit)}
                          disabled={busy || (c.stats?.pending || 0) === 0}
                          data-testid={`campaign-send-batch-${c.campaign_id}`}
                          className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white">
                    <Send className="w-3.5 h-3.5 mr-1" /> Send batch
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openDetail(c.campaign_id)}
                          data-testid={`campaign-detail-${c.campaign_id}`}
                          className="rounded-full">
                    Details
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                <StatCard icon={Users} label="Audience" value={c.total_recipients || 0} testid={`stat-audience-${c.campaign_id}`} />
                <StatCard icon={Send} label="Sent" value={c.stats?.sent || 0} testid={`stat-sent-${c.campaign_id}`} />
                <StatCard icon={Eye} label="Opens" value={`${c.stats?.opened || 0} (${c.stats?.open_rate_pct || 0}%)`} testid={`stat-opens-${c.campaign_id}`} />
                <StatCard icon={MousePointerClick} label="Clicks" value={`${c.stats?.clicked || 0} (${c.stats?.click_rate_pct || 0}%)`} testid={`stat-clicks-${c.campaign_id}`} />
                <StatCard icon={TrendingUp} label="Converted" value={`${c.stats?.converted || 0} (${c.stats?.conversion_rate_pct || 0}%)`} testid={`stat-conv-${c.campaign_id}`} />
                <StatCard icon={Ban} label="Bounced" value={c.stats?.bounced || 0} testid={`stat-bounced-${c.campaign_id}`} />
                <StatCard icon={AlertTriangle} label="Spam" value={c.stats?.spam_reports || 0} testid={`stat-spam-${c.campaign_id}`} />
                <StatCard icon={Mail} label="Unsub." value={c.stats?.unsubscribed || 0} testid={`stat-unsub-${c.campaign_id}`} />
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                Pending: <b>{c.stats?.pending || 0}</b> · Failed: <b>{c.stats?.failed || 0}</b>
              </div>
            </div>
            {selectedId === c.campaign_id && detail && (
              <div className="border-t border-gray-100 p-4 bg-slate-50" data-testid={`campaign-detail-${c.campaign_id}-view`}>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500">Recipient sample (latest 50)</div>
                <div className="mt-2 overflow-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="text-left text-gray-500">
                      <tr>
                        <th className="py-1 pr-3">Email</th>
                        <th className="py-1 pr-3">Status</th>
                        <th className="py-1 pr-3">Sent</th>
                        <th className="py-1 pr-3">Opened</th>
                        <th className="py-1 pr-3">Clicked</th>
                        <th className="py-1 pr-3">Converted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.recipient_sample || []).map((r) => (
                        <tr key={r.user_id + r.email} className="border-t border-gray-200/60">
                          <td className="py-1 pr-3 truncate max-w-[240px]">{r.email}</td>
                          <td className="py-1 pr-3">{r.status}</td>
                          <td className="py-1 pr-3">{r.sent_at ? new Date(r.sent_at).toLocaleDateString() : "—"}</td>
                          <td className="py-1 pr-3">{r.opened_at ? "✓" : "—"}</td>
                          <td className="py-1 pr-3">{r.clicked_at ? "✓" : "—"}</td>
                          <td className="py-1 pr-3">{r.converted_at ? "✓" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
